// External Libraries
import { useState, useRef } from "react";
import ReactRecaptchaV2 from "react-google-recaptcha";

// Custom Components
import InputBox from "../../../common/Forms/InputBox";
import FormGroup from "../../../common/Forms/FormGroup";
import PrimaryButton from "../../../common/Buttons/PrimaryButton";
import { Alert, AlertModel } from "../../../common/Alert/Alert";
import ErrorLabel from "../../../common/Forms/ErrorLabel";

// Stylesheets
import "./contact.css";

// Custom Functions
import handleSubmit from "./handleSubmit";
import { clone } from "../../../../services/utils";


export default function Contact() {
    // Contact Form Data.
    const [contactInfo, setContactInfo] = useState(new ContactInfo());

    // API
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(AlertModel.initialiseAlert());

    // Recaptcha V2... Additional verification if V3 fails.
    const [recaptchaRequired, requireCaptcha] = useState(false);
    const recaptchaRef = useRef(null);

    const handleFormSubmit = async (e) => {
        // Prevent page from refreshing / navigating to another route.
        e.preventDefault();

        const submission = {
            name: contactInfo.name,
            company: contactInfo.company,
            email: contactInfo.email,
            phone: contactInfo.phone,
            message: contactInfo.message
        }

        // If ReCaptcha V3 failed, append ReCaptcha V2 token.
        if (recaptchaRequired) {
            submission.reCaptchaV2Token = recaptchaRef.current.getValue();
            // Hide ReCaptcha component just in-case it needs to reshow.
            requireCaptcha(false);
        }

        // Send the POST request and update related state.
        handleSubmit(submission, setLoading, setAlert, requireCaptcha);
    }

    return (
        <section className="contact-container">
            <div className="contact-card d4-container" id="contact">
                <h1 className="display-6">Get a free design session</h1>
                <form onSubmit={handleFormSubmit}>
                    <FormGroup columns="2" colsGap="2.5%" marginTop="2%">
                        <InputBox label="Name" value={contactInfo.name} onChange={(e) => setContactInfo(contactInfo.updateProperty('name', e.target.value))} required={true} placeholder="Your name" type="text" labelStyle={{ color: "white" }} />
                        <InputBox label="Company" value={contactInfo.company} onChange={(e) => setContactInfo(contactInfo.updateProperty('company', e.target.value))} placeholder="Your company name" type="text" labelStyle={{ color: "white" }} />
                    </FormGroup>
                    <FormGroup columns="2" colsGap="2.5%" marginTop="3%">
                        <InputBox label="Email" value={contactInfo.email} onChange={(e) => setContactInfo(contactInfo.updateProperty('email', e.target.value))} placeholder="Email address" required={true} type="email" labelStyle={{ color: "white" }} />
                        <InputBox label="Phone" value={contactInfo.phone} onChange={(e) => setContactInfo(contactInfo.updateProperty('phone', e.target.value))} placeholder="Phone number" type="text" labelStyle={{ color: "white" }} />
                    </FormGroup>

                    <InputBox label="How can we help?" value={contactInfo.message} onChange={(e) => setContactInfo(contactInfo.updateProperty('message', e.target.value))} placeholder="Enter your message here..." required={true} type="text" multiline="6" labelStyle={{ color: "white", marginTop: "3%" }} />

                    {recaptchaRequired && (
                        <div className="d-flex flex-column align-items-center mt-5">
                            <ReactRecaptchaV2
                                sitekey="6Ld39-klAAAAAGVXckCjcsry-cRleVom-qnhoJtJ"
                                ref={recaptchaRef}
                            />
                            <ErrorLabel message="Please verify that you're a human." />                    
                        </div>
                    )}

                    <div className="d-flex justify-content-center mt-5">
                        <PrimaryButton title="Send a request" loading={loading} />
                    </div>
                </form>
            </div>

            <Alert title={alert.title} message={alert.message} visible={alert.visible} onClose={() => setAlert(AlertModel.initialiseAlert())} />
        </section>
    )
}

// Layer of abstraction rather than many setState methods.
class ContactInfo {
    constructor() {
        this.name = '';
        this.company = '';
        this.email = '';
        this.phone = '';
        this.message = '';
    }

    // Clone a new object and update provided property.
    updateProperty(property, value) {
        let updatedContact = clone(this);
        updatedContact[property] = value;
        return updatedContact;
    }
}